<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue';
import { route as routeFn } from 'ziggy-js';
import { router, usePage } from '@inertiajs/vue3';
import CheckBox from '@/Components/Frontend/Form/CheckBox.vue';
import PasswordInput from '@/Components/Frontend/Form/PasswordInput.vue';
import TextInput from '@/Components/Frontend/Form/TextInput.vue';
import { AnchorLink } from '@/Components/Frontend/Navigation';
import AuthLayout from '@/Layouts/AuthLayout.vue';
import { scrollToFirstErrorMessage } from '@/utils/scroll-to';
import { EnvelopeIcon } from '@heroicons/vue/24/outline';
import { trans as t } from 'laravel-vue-i18n';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

const GoogleButton = defineAsyncComponent(
	() => import('./Partials/GoogleButton.vue'),
);
const AsideContent = defineAsyncComponent(
	() => import('@/Layouts/Partials/AsideContent.vue'),
);

interface Props {
	canResetPassword?: boolean;
	canSso?: boolean;
	status?: string | null;
}

const page = usePage();

const props = withDefaults(defineProps<Props>(), {
	canResetPassword: false,
	status: null,
});

const showStatusNotification = ref(Boolean(props.status));
watch(
	() => props.status,
	(status) => {
		if (status) showStatusNotification.value = true;
	},
);

const profileError = computed<string | null>(
	() => (page.props.errors as Record<string, string>).email ?? null,
);

const isAdminLogin = ref<boolean>();
const canSso = computed(
	() => props.canSso ?? page.props?.feature_flags?.sso ?? false,
);

const schema = yup.object({
	login: yup
		.string()
		.required(t('Required field'))
		.test('login', t('Enter a valid email or username'), function (value) {
			if (!value) return false;

			const isEmail = yup.string().email().isValidSync(value);

			const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
			const isUsername = usernameRegex.test(value);

			return isEmail || isUsername;
		}),
	password: yup.string().required(t('Required field')),
});

const form = useForm({
	initialValues: {
		login: '',
		password: '',
		remember: false,
	},
	validationSchema: schema,
});

const submitForm = form.handleSubmit((values) => {
	router.post(
		routeFn('login'),
		{ ...values, remember: values.remember ? 'on' : '' },
		{
			preserveState: 'errors',
			preserveScroll: true,
			onSuccess: () => {},
			onError: (errors) => {
				form.resetField('password');
				form.setErrors({
					login: errors.login,
					password: errors.password,
				});
				scrollToFirstErrorMessage();
			},
		},
	);
}, scrollToFirstErrorMessage);

onMounted(() => {
	const searchParams = new URLSearchParams(window.location.search);

	isAdminLogin.value = searchParams.get('admin') === 'yes';
});
</script>

<template>
	<AuthLayout
		v-model:show-notification="showStatusNotification"
		:notification-message="status ?? undefined"
	>
		<template #aside-content>
			<AsideContent
				:layout="'auth'"
				:title="$t('Talent acquisition platform')"
				:description="$t('The natural union between talents & organizations')"
			/>
		</template>

		<section
			class="absolute left-1/2 top-14 mx-auto w-[90%] -translate-x-1/2 md:w-full lg:top-1/4"
		>
			<form @submit="submitForm">
				<div class="mb-16 flex flex-col gap-4">
					<span class="text-3xl font-light text-primary">{{
						$t('Login')
					}}</span>
					<span>{{ $t('Scale your team today!') }}</span>
				</div>
				<div
					v-if="profileError"
					role="alert"
					class="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
				>
					{{ profileError }}
				</div>

				<div class="flex flex-col gap-6">
					<div>
						<TextInput
							id="login"
							autofocus
							autocomplete="username"
							name="login"
							:placeholder="`${$t('Company email or username')}`"
							required
							show-field-error
						>
							<template #left> <EnvelopeIcon class="size-6" /> </template>
						</TextInput>
					</div>

					<div>
						<PasswordInput
							id="password"
							name="password"
							type="password"
							:placeholder="`${$t('Password')}`"
							autocomplete="current-password"
							required
							show-field-error
						/>
					</div>
				</div>

				<div class="mt-4 flex justify-between">
					<label class="flex cursor-pointer items-center">
						<CheckBox name="remember" />
						<span class="ms-2 text-sm">{{ $t('Remember me') }}</span>
					</label>
					<AnchorLink
						v-if="canResetPassword"
						:inertia="true"
						:href="route('password.request')"
						:label="$t('Forgot your password?')"
					>
						{{ $t('Forgot your password?') }}
					</AnchorLink>
				</div>

				<!-- Submit Button -->
				<button
					type="submit"
					class="sp-btn my-10 w-full"
					:disabled="form.isSubmitting.value"
				>
					{{ $t('SIGN IN') }}
				</button>
				<div
					v-if="form.isSubmitting.value"
					class="mt-3 text-center text-sm text-gray-400"
					v-text="'Loading...'"
				/>
			</form>

			<div
				class="mb-10 h-3 border-b-2 border-base-200 text-center text-sm text-base-300"
			>
				<span class="bg-white px-5">or</span>
			</div>

			<!--or with Google-->

			<GoogleButton
				v-if="canSso && isAdminLogin"
				label="LOGIN WITH GOOGLE ACCOUNT"
			/>

			<div
				class="mt-8 text-center text-sm font-semibold uppercase text-base-300"
			>
				{{ $t('Don’t Have an account?') }}
				<AnchorLink
					class="text-primary"
					:inertia="true"
					:href="route('onboarding.index')"
				>
					{{ $t('Create a vendor account') }}
				</AnchorLink>
			</div>
		</section>
	</AuthLayout>
</template>
